import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

import { logEvent, type EventType } from "./eventsApi";

/**
 * How long a record is held before it is written.
 *
 * Care Events are append-only, so the cheapest honest undo is to not write yet.
 * Material's snackbar defaults to 1.5–2.75s, which is too short to read and
 * react to; Gmail's Undo Send offers 5–30s. Eight seconds sits in that range and
 * is long enough to notice a mis-tap on a balcony.
 *
 * This is deliberately not the only way to fix a mistake — a record past the
 * window is corrected by appending a correction. A time-limited undo as the
 * sole correction path would also fail WCAG 2.2.1.
 */
const GRACE_MS = 8000;

export interface Pending {
  key: string;
  specimenId: string;
  type: EventType;
  payload: Record<string, unknown>;
  /** What the row should say while this is in flight. */
  label: string;
  at: Date;
}

interface Logger {
  /** Records something, after a grace window in which it can be undone. */
  log: (specimenId: string, type: EventType, payload: Record<string, unknown>, label: string, occurredAt?: Date) => void;
  undo: (key: string) => void;
  /** Pending records keyed by specimen, so a row can render its own state. */
  pendingFor: (specimenId: string) => Pending[];
  pending: Pending[];
}

const Context = createContext<Logger | null>(null);

export function LoggerProvider({ children, onCommitted }: { children: React.ReactNode; onCommitted: () => void }) {
  const [pending, setPending] = useState<Pending[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const log = useCallback<Logger["log"]>(
    (specimenId, type, payload, label, occurredAt) => {
      const key = `${specimenId}-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const at = occurredAt ?? new Date();

      // Shown immediately. The record the user reads is the row changing, not a
      // message saying "saved" — people expect success and only need telling
      // when something fails.
      setPending((current) => [...current, { key, specimenId, type, payload, label, at }]);

      // Haptic where available: the phone may be at arm's length over a pot.
      if ("vibrate" in navigator) navigator.vibrate?.(12);

      timers.current.set(
        key,
        setTimeout(() => {
          timers.current.delete(key);
          void logEvent(specimenId, type, payload, at.toISOString())
            .then(() => {
              setPending((current) => current.filter((item) => item.key !== key));
              onCommitted();
            })
            .catch(() => {
              // Keep it visible rather than silently losing it. The row will
              // still show the reading; a reload reconciles against the server.
              setPending((current) =>
                current.map((item) => (item.key === key ? { ...item, label: `${item.label} — not saved` } : item)),
              );
            });
        }, GRACE_MS),
      );
    },
    [onCommitted],
  );

  const undo = useCallback((key: string) => {
    const timer = timers.current.get(key);
    // Undo inside the window means the record was never written at all, so the
    // append-only history stays clean of mistakes the user caught themselves.
    if (timer) clearTimeout(timer);
    timers.current.delete(key);
    setPending((current) => current.filter((item) => item.key !== key));
  }, []);

  const value = useMemo<Logger>(
    () => ({
      log,
      undo,
      pending,
      pendingFor: (specimenId) => pending.filter((item) => item.specimenId === specimenId),
    }),
    [log, undo, pending],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useLogger(): Logger {
  const value = useContext(Context);
  if (!value) throw new Error("useLogger must be used inside LoggerProvider");
  return value;
}

export { GRACE_MS };
